import { getLocalStandardDate } from '@tesla/design-system';
import { iconCalendar } from '@tesla/design-system-icons';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { dateToIsoDateString, isDateSelected, DatePicker, getDatePickerDate, } from '../date-picker/index.js';
import { Tooltip, useTooltipState } from '../tooltip/index.js';
import { useId } from '../hooks/use-id.js';
import { Icon } from '../icon/index.js';
import { IconButton } from '../icon-button/index.js';
import { DatePart, checkDayRange, cursorToPosition, datePartsToDate, datePartsToString, dateToDateParts, fixZeroInDateParts, getCorrectDatePartValue, getDatePartValue, getLocalePlaceholder, getUpdatedDatePart, setLocaleDateParts, } from './manual-entry-utils.js';
export const FormInputDate = ({ className, date, dateDisabled, dateType, disabled, firstDayOfWeek = 0, id, inputRef, leading, trailing, locale = 'en-US', manualEntry, name, onBlur, onDateChange, onDatePickerBlur, open, selectOnFocus, readOnly, value: externalValue, variant = 'default', datePickerProps, tooltipProps, min, max, ...rest }) => {
    const { expanded, handlers, setExpanded, toggleExpanded, wrapperRef } = useTooltipState();
    // Close on user or parent input
    useEffect(() => {
        setExpanded(false);
    }, [date, setExpanded]);
    const value = externalValue ||
        (isDateSelected(date, dateType)
            ? getLocalStandardDate(getDatePickerDate(date, dateType), locale)
            : '');
    const uId = useId();
    const htmlId = id || uId;
    const willSelectOnFocus = selectOnFocus !== null && selectOnFocus !== void 0 ? selectOnFocus : readOnly;
    const fallbackRef = useRef(null);
    const ref = inputRef || fallbackRef;
    // dateParts is a dict for storing each date part value
    const [dateParts, setDateParts] = useState(setLocaleDateParts(locale));
    const [selectedPosition, setSelectedPosition] = useState(0);
    // cursorRange is a array with length 2 that stands for start and end position of highlighted area
    const [cursorRange, setCursorRange] = useState([null, null]);
    // reset is used to allow restart of entering values
    // With reset false, entering 1, 2 will become 12
    // With reset true, entering 1, 2 will become 2 (1 reseted)
    const [reset, setReset] = useState(false);
    // displayValue is what is displayed in the input box
    const [displayValue, setDisplayValue] = useState(getLocalePlaceholder(locale));
    // Overwrite default cursor position with highlight area defined by cursorRange
    // Required for setting the correct highlight area when rendering
    useEffect(() => {
        // Has to be in useEffect, otherwise it will not update cursorRange sometimes
        if (manualEntry) {
            const input = ref === null || ref === void 0 ? void 0 : ref.current;
            input === null || input === void 0 ? void 0 : input.setSelectionRange(cursorRange[0], cursorRange[1]);
        }
    });
    // Initialize the cursor ranges
    // Required for parsing the correct datePart format when locale is updated
    useEffect(() => {
        if (manualEntry) {
            // Get datePart format when locale is changed
            const newDateParts = setLocaleDateParts(locale);
            setDateParts(newDateParts);
            setDisplayValue(datePartsToString(newDateParts));
        }
    }, [manualEntry, locale]);
    // Update dateParts to match the date
    // Required for setting the dateParts to match date when date is updated
    useEffect(() => {
        if (manualEntry && date) {
            // Set dateParts to the new date
            const newDateParts = dateToDateParts(dateParts, date);
            setDateParts(newDateParts);
            setDisplayValue(datePartsToString(newDateParts));
        }
    }, [date, dateParts, manualEntry]);
    // Update selected date part
    // Required for setting the highlighted area based on passed in position
    // Called after updating any dateParts to make sure the correct datePart is being highlighted
    const updateSelection = (position) => {
        const min = 0;
        const max = Object.keys(DatePart).length - 1;
        // Limit position from 0 to number of date parts
        if (position > max)
            position = max;
        if (position < min)
            position = min;
        // Set cursorRange to the passed in position
        setCursorRange([dateParts[position].cursorStart, dateParts[position].cursorEnd]);
        const positionUpdate = position !== selectedPosition;
        setReset(positionUpdate);
        setSelectedPosition(position);
        if (positionUpdate)
            handleZeroInDateParts();
    };
    // Update displaying value
    // Required for updating the value of a datePart
    const updateDisplayValue = (position, value, cap = true) => {
        if (!value) {
            dateParts[position].value = null;
        }
        else {
            if (parseInt(value) === 0) {
                // Do not check range when value is 0
                dateParts[position].value = value;
            }
            else if (parseInt(value)) {
                dateParts[position].value = getCorrectDatePartValue(dateParts, parseInt(value), position, cap).toString();
                // Re-check date range since date range differs when month changes
                const [dayPart, dayIndex] = checkDayRange(dateParts);
                if (dayPart)
                    dateParts[dayIndex] = dayPart;
            }
        }
        const newDate = datePartsToDate(dateParts);
        // Update the date value by calling onDateChange
        if (onDateChange)
            dateType === 'string' ? onDateChange(dateToIsoDateString(newDate)) : onDateChange(newDate);
        setDisplayValue(datePartsToString(dateParts));
    };
    // Handle any zeros in dateParts
    // Required for updating 0 in dateParts to 1 to make sure the values are valid
    // Called onBlur and when highlighted position changes
    const handleZeroInDateParts = () => {
        // Set all 0 in dateParts to 1
        const newDateParts = fixZeroInDateParts(dateParts);
        setDateParts(newDateParts);
        setDisplayValue(datePartsToString(newDateParts));
    };
    // Handle User Input
    // Required for handling user input by appending input value to prevValue
    // Called on any value change in the input component
    const handleChange = (e) => {
        const prevValue = dateParts[selectedPosition].value;
        let value = getDatePartValue(e.target.value, selectedPosition, dateParts);
        // Do not update value when input key is null
        if (value === null) {
            updateSelection(selectedPosition);
            // Keep reset the same when value is invalid
            setReset(reset);
        }
        else {
            const datePartLength = dateParts[selectedPosition].placeholder.length;
            if (!reset && prevValue !== undefined) {
                value = prevValue + value;
            }
            updateDisplayValue(selectedPosition, value);
            // Update highlighted area to next datePart when
            // 1. dateParts length is larger than placeholder
            // 2. any value added will exceed the max like 3 in month should go to next datePart
            if (value.length >= datePartLength) {
                updateSelection(selectedPosition + 1);
                setReset(true);
            }
            else {
                if (parseInt(value) > dateParts[selectedPosition].max / 10)
                    updateSelection(selectedPosition + 1);
                else
                    updateSelection(selectedPosition);
            }
        }
    };
    // Handle key down for manualEntry
    // Required for handling key downs when manualEntry is true
    // Called when user press any key
    // Up: increase value
    // Down: decrease value
    // Left: go to left datePart until the first datePart
    // Rigtht: go to right datePart until the last datePart
    // Tab: got to right datePart and next component
    // Delete or Backspace: remove the value to be palceholder
    const handleManualEntryKeyDown = (e) => {
        const dir = wrapperRef.current ? getComputedStyle(wrapperRef.current).direction : undefined;
        if (cursorRange[0] !== null && cursorRange[1] !== null) {
            switch (e.key) {
                case 'ArrowDown':
                    // ArrowDown will decrease the selected date part by 1
                    e.preventDefault();
                    updateDisplayValue(selectedPosition, getUpdatedDatePart(dateParts, selectedPosition, -1, 'ArrowDown'));
                    setReset(true);
                    break;
                case 'ArrowLeft':
                    // ArrowLeft will move the selected part to the left one
                    e.preventDefault();
                    updateSelection(dir === 'rtl' ? selectedPosition + 1 : selectedPosition - 1);
                    break;
                case 'ArrowRight':
                    // ArrowRight will move the selected part to the right one
                    e.preventDefault();
                    updateSelection(dir === 'rtl' ? selectedPosition - 1 : selectedPosition + 1);
                    break;
                case 'ArrowUp':
                    // ArrowUp will increase the selected date part by 1
                    e.preventDefault();
                    updateDisplayValue(selectedPosition, getUpdatedDatePart(dateParts, selectedPosition, 1, 'ArrowUp'));
                    setReset(true);
                    break;
                case 'Backspace':
                case 'Delete':
                    e.preventDefault();
                    updateDisplayValue(selectedPosition, null);
                    break;
                case 'Tab':
                    if (e.shiftKey) {
                        if (selectedPosition > 0) {
                            e.preventDefault();
                            updateSelection(selectedPosition - 1);
                        }
                        break;
                    }
                    if (selectedPosition < 2) {
                        e.preventDefault();
                        updateSelection(selectedPosition + 1);
                    }
                    break;
            }
        }
        switch (e.key) {
            case ' ':
                e.preventDefault();
                break;
            case 'Enter':
                setExpanded(true);
                break;
            case 'Return':
                setExpanded(true);
                break;
            default:
                break;
        }
        handlers.onKeyDown(e);
    };
    // Extend default tooltip keyboarding
    const handleKeyDown = (e) => {
        switch (e.key) {
            case ' ':
                e.preventDefault();
                setExpanded(true);
                break;
            case 'ArrowDown':
            case 'Enter':
            case 'Return':
                setExpanded(true);
                break;
            default:
                break;
        }
        // @ts-expect-error - React's event types are not self-compatible in this case
        handlers.onKeyDown(e);
    };
    // Manual entry onClick for the component triggers auto-select of date part based on the cursorPos
    // Setting the selected date part based on where the user clicks.
    const handleManualEntrySelect = (event) => {
        const cursorPos = event.target.selectionStart;
        if (cursorPos !== null) {
            updateSelection(cursorToPosition(dateParts, cursorPos));
            setReset(true);
        }
    };
    // Handle manual entry on blur.
    // Required for resetting the value, cursorRange, and handle any 0 value in dateParts
    const handleManualEntryBlur = (e) => {
        setReset(true);
        setCursorRange([null, null]);
        handleZeroInDateParts();
        handleBlur(e);
    };
    // onBlur for the component should only fire when the user is leaving the component
    // since onBlurs can happen as the user clicks around within the component,
    // we have to handle the behavior explicitly
    const handleBlur = (e) => {
        // Note: related target represents the item focused-to in the context of a blur event
        if (wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget)) {
            if (onBlur)
                onBlur(e);
        }
    };
    return (React.createElement("div", { className: cx('tds-form-input', {
            [`tds-form-input--${variant}`]: variant,
            'tds-form-input--readonly': readOnly,
            'tds--disabled': disabled,
        }, className), onBlur: manualEntry ? handleManualEntryBlur : handleBlur, onKeyDown: manualEntry ? handleManualEntryKeyDown : handleKeyDown, onClick: (e) => {
            var _a, _b, _c;
            // Autofocus input when outer div is clicked
            if (!e.target.closest('button, .tds-tooltip-wrapper') ||
                willSelectOnFocus) {
                (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
                // Auto-select text within the input when focused
                if (willSelectOnFocus && !((_b = document.getSelection()) === null || _b === void 0 ? void 0 : _b.toString().length)) {
                    (_c = ref.current) === null || _c === void 0 ? void 0 : _c.select();
                }
            }
        }, ref: wrapperRef },
        !!leading && React.createElement("div", { className: "tds-form-input-leading" }, leading),
        React.createElement("input", { autoComplete: "off", className: cx('tds-form-input-text tds-form-input-date', {
                ['tds-form-input-date--placeholder']: manualEntry && displayValue === getLocalePlaceholder(locale),
            }), id: htmlId, name: name, onChange: handleChange, onClick: manualEntry ? undefined : toggleExpanded, onSelect: manualEntry ? handleManualEntrySelect : undefined, readOnly: !manualEntry || readOnly, disabled: disabled, ref: ref, type: "text", value: manualEntry ? displayValue : value, ...rest }),
        React.createElement("div", { className: "tds-form-input-trailing" },
            trailing,
            !readOnly && (React.createElement(IconButton, { onClick: toggleExpanded, disabled: disabled },
                React.createElement(Icon, { data: iconCalendar, inline: true })))),
        !readOnly && (React.createElement(Tooltip, { className: "tds-date-picker-tooltip", open: open !== null && open !== void 0 ? open : expanded, orientation: "down", ...tooltipProps },
            React.createElement(DatePicker, { date: date, dateType: dateType, density: "dense", disabled: dateDisabled, firstDayOfWeek: firstDayOfWeek, locale: locale, onBlur: onDatePickerBlur, onDateChange: onDateChange, min: min, max: max, ...datePickerProps })))));
};
